// Sub-paso 5.3.2: SupervisorZonaService.js (30 min)
// DÍA 5: Dashboards Especializados por Rol - PASO 5.3 Supervisor de Campo Service
// INTEGRACIÓN BACKEND REAL - PASO 1 Y 2

import { BaseRoleService } from './BaseRoleService.js'
import { apiClient } from '@/services/api-client'

/**
 * Servicio especializado para Supervisor de Zona
 * Extiende BaseRoleService con funcionalidades específicas para supervisores de campo
 */
export class SupervisorZonaService extends BaseRoleService {
  constructor() {
    // P13-V7B (refutation): the canonical role name; the legacy alias 'supervisor_zona' decides nothing.
    super('field_supervisor')
    this.apiClient = apiClient
  }

  // ===============================
  // 🏢 GESTIÓN DE AGENCIAS
  // ===============================

  /**
   * Obtener agencias del grupo asignado al supervisor
   */
  async getGroupAgencies(groupId) {
    try {
      console.log('🏢 Obteniendo agencias asignadas (backend real)')

      // Backend real: GET /api/field-supervisor/assigned-agencies -> { success, data: { agencies: [...] } }
      const response = await this.apiClient.get(`/field-supervisor/assigned-agencies`, {
        headers: this.getAuthHeaders()
      })

      const agencies = response.data?.data?.agencies || response.data?.agencies || response.data || []
      console.log('✅ Agencias obtenidas:', agencies.length)

      // Normalizar propiedades mínimas esperadas por el dashboard
      const normalized = agencies.map(a => ({
        id: a.id,
        name: a.name,
        address: a.address,
        // Mapear estados del backend a los usados en UI
        status: a.status === 'with_incident' ? 'pending' : (a.status || 'operational'),
        lastVisit: a.last_visit || a.lastVisit,
        issues: typeof a.pending_incidents === 'number' ? a.pending_incidents : (a.issues || 0),
        manager: a.manager || a.agency_manager || undefined,
        groupId: a.groupId || a.group?.id,
        // Incluir objeto group para mostrar nombre del grupo en UI
        group: a.group || (a.groupId ? { id: a.groupId, name: null } : undefined)
      }))

      return this.processAgenciesData(normalized)
      
    } catch (error) {
      console.error('❌ Error obteniendo agencias del grupo:', error)
      
      // NO usar fallback - devolver vacío en caso de error
      return []
    }
  }

  // P13-V7B: `updateAgency`, `changeAgencyStatus` and `createRoute` were REMOVED. They wrote to
  // `PUT /supervisor/agencies/:id`, `PATCH /supervisor/agencies/:id/status` and `POST /supervisor/routes`.
  // The backend retired the whole `/api/supervisor` family (every path answers 410), no surface
  // calls them, and two of them ended in an undefined `handleServiceError`, so a rejected write
  // threw a TypeError instead of reporting anything.

  // P13-V7B: `getActiveRoute` was REMOVED with the landing branch that was its only caller. It read
  // `GET /field-supervisor/daily-route`, which the backend refuses to this role and answers 501 to
  // the vendor, and it defaulted an absent estimate to 180 minutes. The daily route belongs to V4;
  // its reader is written when that capability exists.

  // P13-V7B: `updateVisitStatus`, `getGroupEmployees` and `updateEmployeeSchedule` were REMOVED with
  // the retired `/api/supervisor` family they targeted (`PATCH /supervisor/visits/:id/status`,
  // `GET /supervisor/groups/:id/employees`, `PUT /supervisor/employees/:id/schedule`). Their only
  // callers were template branches of the landing that no state could ever reach.

  // ===============================
  // 💰 GESTIÓN DE GASTOS FIJOS
  // ===============================

  /**
   * Obtener gastos fijos pendientes de las agencias del supervisor
   */
  async getPendingExpenses() {
    try {
      console.log('💰 Obteniendo gastos fijos pendientes')
      
      // Intentar obtener del backend real
      const response = await this.apiClient.get('/field-supervisor/expenses/pending', {
        headers: this.getAuthHeaders()
      })
      
      const pending = response.data?.data?.pending || response.data?.pending || []
      console.log('✅ Gastos pendientes obtenidos:', pending.length)
      
      return { data: { pending } }
    } catch (error) {
      console.warn('⚠️ Endpoint de gastos pendientes no disponible, devolviendo vacío')
      // Devolver estructura vacía en lugar de fallar
      return { data: { pending: [] } }
    }
  }

  /**
   * Obtener información del grupo del supervisor (incluye fondo disponible)
   */
  async getGroupInfo() {
    try {
      console.log('💰 Obteniendo información del grupo')
      
      const response = await this.apiClient.get('/field-supervisor/group-info', {
        headers: this.getAuthHeaders()
      })
      
      return response.data || response
    } catch (error) {
      console.warn('⚠️ Error obteniendo info del grupo:', error.message)
      return { fondo_disponible: 0, groups: [] }
    }
  }

  // P13-V7B: `getGroupIncidents` was REMOVED. Its only caller was the landing, which used the rows
  // for nothing but a count chip that did not describe what the map plots. The reader defaulted
  // absent fields into facts ('medium', 'open', the current time as the report date). The zone's
  // incidents are read by the map and by the canonical inboxes through their own adapters.

  // P13-V1D (C10, R0-INC-09): el método phantom validateIncident (endpoint /supervisor/incidents/* que
  // nunca existió en el backend) fue RETIRADO. La superficie canónica es IncidentInbox.

  // P13-V1D (C10, R0-INC-09): el método phantom escalateIncident (endpoint /supervisor/incidents/* que
  // nunca existió en el backend) fue RETIRADO. La superficie canónica es IncidentInbox.

  // P13-V7B: `createDepartmentReport` was REMOVED (`POST /supervisor/reports/departmental`, retired
  // family, no caller).

  // ===============================
  // 🔄 MÉTODOS DE PROCESAMIENTO
  // ===============================

  processAgenciesData(agencies) {
    return agencies.map(agency => ({
      ...agency,
      statusColor: this.getAgencyStatusColor(agency.status),
      lastVisitFormatted: agency.lastVisit ? 
        new Date(agency.lastVisit).toLocaleDateString('es-ES') : 
        'Sin visitas'
    }))
  }

  // ===============================
  // 🎨 MÉTODOS DE UTILIDAD
  // ===============================

  getAgencyStatusColor(status) {
    const colors = {
      operational: 'success',
      maintenance: 'warning',
      closed: 'error',
      pending: 'info'
    }
    return colors[status] || 'grey'
  }

}

// Singleton para uso global
export const supervisorZonaService = new SupervisorZonaService()
export default SupervisorZonaService
